import { useParams, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FUNC_GROUPS, FUNC_META, casesForFunc } from "@/data/useCases";
import { FUNC_ICONS } from "@/components/LineIcons";
import { useBookCall } from "@/components/BookCallContext";
import CSATGauge from "@/components/CSATGauge";
import { TESTIDS } from "@/constants/testIds";

/**
 * Function-group page, matches Practices / Industries layout:
 *   - Slim horizontal switcher (the 4 functional groups) at the very top.
 *   - Two-column hero (title + copy + CTA on the left, image on the right).
 *   - Repeated body copy.
 *   - Grid of agent cards inside this group. Clicking a card → agent detail.
 */
export default function UseCaseGroup() {
    const { id } = useParams();
    const groupId = FUNC_GROUPS.some((g) => g.id === id) ? id : null;
    const navigate = useNavigate();
    const { openModal } = useBookCall();

    if (!groupId) return <Navigate to="/use-cases/listen" replace />;

    const meta = FUNC_META[groupId];
    const accent = meta.accent;
    const agents = casesForFunc(groupId);

    return (
        <div data-testid={TESTIDS.uclist.root}>
            {/* Slim switcher — "Explore use cases" label removed per feedback */}
            <div style={switcherBar}>
                <div className="wrap" style={switcherWrap}>
                    <div
                        className="siblings-row"
                        style={{
                            display: "flex",
                            gap: 8,
                            overflowX: "auto",
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {FUNC_GROUPS.map((g) => {
                            const on = g.id === groupId;
                            const col = FUNC_META[g.id].accent;
                            return (
                                <Link
                                    key={g.id}
                                    to={`/use-cases/${g.id}`}
                                    data-testid={TESTIDS.uclist.funcTab(g.id)}
                                    style={pillStyle(on, col)}
                                >
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            color: on ? col : "var(--kl-muted)",
                                            flexShrink: 0,
                                        }}
                                        className="switcher-icon"
                                    >
                                        {FUNC_ICONS[g.id]}
                                    </span>
                                    <span
                                        style={{
                                            whiteSpace: "nowrap",
                                            fontSize: ".82rem",
                                        }}
                                    >
                                        {g.name}
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Two-column hero */}
            <div className="phead" style={{ paddingTop: 40, paddingBottom: 40 }}>
                <div className="wrap" style={{ position: "relative", zIndex: 2 }}>
                    <div style={heroGrid} className="ed-hero">
                        <motion.div
                            key={groupId + "-copy"}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <span
                                className="kicker"
                                style={{ color: accent, textTransform: "uppercase" }}
                            >
                                Use Case Group
                            </span>
                            <h1 className="h1" style={{ margin: "14px 0 14px" }}>
                                {meta.name}
                            </h1>
                            <p
                                className="font-mono-label"
                                style={{
                                    color: accent,
                                    fontSize: ".82rem",
                                    letterSpacing: ".1em",
                                    textTransform: "uppercase",
                                    marginBottom: 22,
                                    maxWidth: 500,
                                }}
                            >
                                {meta.short}
                            </p>
                            <p
                                style={{
                                    color: "var(--ink)",
                                    fontSize: "1rem",
                                    lineHeight: 1.65,
                                    marginBottom: 28,
                                    maxWidth: 520,
                                }}
                            >
                                This group includes {agents.length} KyroLabs agents, each built,
                                deployed and operated on our platform. Select any card to explore
                                how the agent works, the problem it solves and what deployment
                                looks like.
                            </p>
                            <button
                                className="btn btn-p"
                                onClick={() => openModal({ source: `uc-group:${groupId}` })}
                            >
                                Book a Demo <ArrowRight size={14} />
                            </button>
                        </motion.div>
                        <motion.div
                            key={groupId + "-img"}
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            style={heroImageWrap}
                            aria-hidden
                        >
                            <picture>
                                <source type="image/webp" srcSet={meta.image} />
                                <img
                                    src={meta.image}
                                    alt=""
                                    loading="eager"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                    }}
                                />
                            </picture>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Body + agent grid */}
            <section style={{ padding: "40px 0 90px" }}>
                <div className="wrap">
                    <h3
                        className="font-mono-label"
                        style={{ color: "var(--dim)", marginBottom: 18 }}
                    >
                        Agents in this group
                    </h3>

                    <div
                        data-testid={TESTIDS.uclist.grid}
                        style={agentsGrid}
                        className="uc-grid"
                    >
                        {agents.map((u, i) => (
                            <motion.button
                                key={u.id}
                                data-testid={TESTIDS.uclist.card(u.id)}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.32, delay: i * 0.02 }}
                                onClick={() => navigate(`/use-cases/${u.id}`)}
                                style={agentCard(accent)}
                            >
                                <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
                                    {u.id === "csat" ? (
                                        <div style={miniGaugeWrap(accent)}>
                                            <CSATGauge value={92} accent={accent} size={70} />
                                        </div>
                                    ) : (
                                        <div style={agentBadge(accent)}>
                                            <span
                                                style={{
                                                    fontFamily: "'JetBrains Mono', monospace",
                                                    fontSize: ".62rem",
                                                    letterSpacing: ".14em",
                                                    color: accent,
                                                }}
                                            >
                                                {u.name.slice(0, 2).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div style={{ minWidth: 0, textAlign: "left" }}>
                                        <b
                                            className="font-display"
                                            style={{
                                                display: "block",
                                                fontWeight: 600,
                                                fontSize: "1rem",
                                                marginBottom: 4,
                                            }}
                                        >
                                            {u.name}
                                        </b>
                                        <span
                                            style={{
                                                color: "var(--kl-muted)",
                                                fontSize: ".86rem",
                                                lineHeight: 1.5,
                                                display: "block",
                                            }}
                                        >
                                            {u.one}
                                        </span>
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </div>

                <style>{`
                    .switcher-icon svg { width: 16px; height: 16px; }
                    @media (max-width: 900px) {
                        .ed-hero { grid-template-columns: 1fr !important; gap: 28px !important; }
                        .uc-grid { grid-template-columns: 1fr 1fr !important; }
                    }
                    @media (max-width: 540px) {
                        .uc-grid { grid-template-columns: 1fr !important; }
                    }
                `}</style>
            </section>
        </div>
    );
}

/* ---------- styles ---------- */

const switcherBar = {
    borderBottom: "1px solid var(--line)",
    background: "rgba(255,255,255,0.015)",
    padding: "10px 0",
};
const switcherWrap = { display: "flex", alignItems: "center", gap: 16 };
const pillStyle = (on, col) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 12px",
    borderRadius: 999,
    border: on ? `1px solid ${col}` : "1px solid var(--line)",
    background: on ? `${col}15` : "transparent",
    color: on ? "var(--ink)" : "var(--kl-muted)",
    textDecoration: "none",
    fontWeight: 500,
    transition: "all .16s",
    boxShadow: on ? `0 0 0 3px ${col}18` : "none",
    flexShrink: 0,
});

const heroGrid = {
    display: "grid",
    gridTemplateColumns: "1.2fr 1fr",
    gap: 40,
    alignItems: "center",
};
const heroImageWrap = {
    aspectRatio: "16 / 10",
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid var(--line)",
    background: "#0d1116",
    maxWidth: 560,
    marginLeft: "auto",
    width: "100%",
};

const agentsGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 14,
};

const agentCard = (accent) => ({
    padding: "18px 20px",
    borderRadius: 14,
    border: "1px solid var(--line)",
    background: "rgba(255,255,255,0.025)",
    cursor: "pointer",
    font: "inherit",
    color: "var(--ink)",
    transition: "border-color .16s, transform .16s, background .16s",
});

const agentBadge = (accent) => ({
    width: 44,
    height: 44,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: `radial-gradient(65% 65% at 40% 40%, ${accent}30, ${accent}0a 70%)`,
    border: `1px solid ${accent}40`,
    flexShrink: 0,
});
const miniGaugeWrap = (accent) => ({
    width: 78,
    height: 44,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
});
